/**
 * Workspace resolution.
 *
 * Resolution order:
 * 1. --workspace flag (explicit)
 * 2. ELECTRIC_WORKSPACE_ID env var
 * 3. API token auth → call whoami, extract workspaceId
 * 4. User JWT + single workspace → auto-select
 * 5. User JWT + multiple workspaces → error with guidance
 */

import type { ContractRouterClient } from '@orpc/contract'
import type { Contract } from 'admin-api-contract'

export async function resolveWorkspace(
  client: ContractRouterClient<Contract>,
  explicitWorkspace?: string
): Promise<string> {
  // 1. Explicit flag
  if (explicitWorkspace) {
    return explicitWorkspace
  }

  // 2. Env var
  const envWorkspace = process.env.ELECTRIC_WORKSPACE_ID
  if (envWorkspace) {
    return envWorkspace
  }

  // 3+4. Call whoami first to check auth type
  const whoami = await client.auth.whoami({})

  if (whoami.type === `token`) {
    // 3. API token — use token's workspace
    return whoami.workspaceId
  }

  // 4+5. User JWT — list workspaces
  const { workspaces } = await client.workspaces.list({})

  if (workspaces.length === 0) {
    throw new Error(
      `No workspaces found. Create one at https://dashboard.electric-sql.cloud`
    )
  }

  if (workspaces.length === 1) {
    return workspaces[0].id
  }

  // 5. Multiple workspaces — error with guidance
  const list = workspaces.map((w) => `  ${w.id}  ${w.name}`).join(`\n`)

  throw new Error(
    `Multiple workspaces found. Specify one with --workspace or ELECTRIC_WORKSPACE_ID.\n\nAvailable workspaces:\n${list}\n\nSet a default: export ELECTRIC_WORKSPACE_ID=<id>`
  )
}
